import type { UndoAction } from '@/lib/history/types/undo-action'
import type { UndoAtomicAction } from '@/lib/history/types/undo-atomic-action'
import type { ShapeListOp } from '@/lib/shapes/types/shape-list-op'
import type { HistoryDirection } from '@/lib/history/types/history-direction'

export function orderedCompoundReplayActions(
  action: UndoAction,
  direction: HistoryDirection
): UndoAtomicAction[] {
  if (action.type !== 'compound') return []
  const ordered = direction === 'undo' ? action.actions : [...action.actions].reverse()
  // Selection replay depends on the target shape ids existing. In command transactions
  // (group/ungroup), run structural actions first and apply selection snapshots last.
  const nonSelection = ordered.filter((subAction) => subAction.type !== 'selection')
  const selectionOnly = ordered.filter((subAction) => subAction.type === 'selection')

  // add/remove sub-actions with a recorded insertionIndex must replay in ascending
  // insertionIndex order (their original z-order), not LIFO-pop order -- otherwise a group
  // delete's undo corrupts stacking order (WMPCM-5019/7165). Text frames are shapes now
  // (text-unification), so the `textAdd`/`textRemove` half of this ordering hazard collapsed into
  // the plain add/remove case rather than going away -- a deleted group with text members still
  // has to come back at its original z-order.
  const insertionIndexOf = (a: UndoAtomicAction): number | null => {
    if (a.type === 'add' || a.type === 'remove') {
      return a.insertionIndex ?? null
    }

    return null
  }
  // Reordered by partitioning the orderable entries out and putting them back into the same slots,
  // NOT by sorting the whole list with a comparator that returns 0 for pairs it can't rank
  // (WMPCM-7036). Such a comparator is intransitive -- "A before B" cannot propagate through an
  // element that compares equal to both -- so the sort silently degraded to a no-op the moment
  // anything else was interleaved. A linked-frame delete produces exactly that shape
  // (`remove` -> `update` -> `textLinkChange` -> `update`, per deleted frame), so
  // undoing a multi-frame linked delete restored the frames at the wrong z-order. Entries without
  // an insertionIndex keep their positions, which is what the original "left in place" intent was.
  const orderableSlots: number[] = []

  nonSelection.forEach((subAction, slot) => {
    if (insertionIndexOf(subAction) !== null) orderableSlots.push(slot)
  })

  // Both sides are guaranteed to have an index here, so this comparator is total -- the property
  // the original one lacked. `sort` is stable, so equal indices keep their relative order.
  const slotsByInsertionIndex = [...orderableSlots].sort(
    (a, b) => insertionIndexOf(nonSelection[a]!)! - insertionIndexOf(nonSelection[b]!)!
  )
  const reordered = [...nonSelection]

  slotsByInsertionIndex.forEach((sourceSlot, i) => {
    reordered[orderableSlots[i]!] = nonSelection[sourceSlot]!
  })

  return [...reordered, ...selectionOnly]
}

export function shapeListOpForAction(
  subAction: UndoAtomicAction,
  direction: HistoryDirection
): ShapeListOp | null {
  if (subAction.type === 'add') {
    return direction === 'undo'
      ? { kind: 'remove', shapeId: subAction.shape.id }
      : { kind: 'insert', shape: subAction.shape, insertionIndex: subAction.insertionIndex }
  }

  if (subAction.type === 'remove') {
    return direction === 'undo'
      ? { kind: 'insert', shape: subAction.shape, insertionIndex: subAction.insertionIndex }
      : { kind: 'remove', shapeId: subAction.shape.id }
  }

  return null
}
